/**
 * OPS-WS-1 / OPS-WS-3: workspace 一覧テーブル。
 * sales_counter 単体前提へ縮退（#1479、カウンターシリーズ4製品構想は2026-07-02撤退。
 * 賃貸固有列（物件DB/内見要求30d/断定回避発火率）は削除済）。
 *
 * カラム:
 *   - 製品（固定表示: 営業）
 *   - workspace 表示名（name → slug フォールバック）
 *   - 課金状態
 *   - 最終ログイン（相対表示）
 *   - AI ON/OFF
 *   - チャネル接続数
 *   - 7d 受信件数
 *   - 30d 受信件数
 *   - AI 採用率
 *   - 健康度シグナル（バッジ）
 */
import { Link } from 'react-router';
import type {
  HealthFilterKey,
  WorkspaceHealthRow,
  WorkspaceListParams,
  WorkspaceSortKey,
} from '../../lib/workspaces-api';
import {
  BILLING_STATUS_LABEL,
  formatRelative,
  HEALTH_LABEL,
  PRODUCT_EMAIL_CONSENT_BADGE,
  PRODUCT_EMAIL_CONSENT_LABEL,
  PRODUCT_LABEL,
  SORT_LABEL,
} from './labels';

export interface WorkspaceTableProps {
  rows: WorkspaceHealthRow[];
  total: number;
  page: number;
  pageSize: number;
  sort: WorkspaceSortKey | undefined;
  order: 'asc' | 'desc' | undefined;
  onParamsChange: (next: WorkspaceListParams) => void;
  params: WorkspaceListParams;
}

const STATUS_BADGE: Record<string, string> = {
  active: 'bg-emerald-100 text-emerald-800',
  trialing: 'bg-sky-100 text-sky-800',
  past_due: 'bg-rose-100 text-rose-800',
  canceled: 'bg-slate-100 text-slate-600',
};

const SORT_COLUMNS: ReadonlyArray<WorkspaceSortKey> = [
  'lastLogin',
  'createdAt',
  'leadsLast7d',
  'leadsLast30d',
];

export function WorkspaceTable({
  rows,
  total,
  page,
  pageSize,
  sort,
  order,
  onParamsChange,
  params,
}: WorkspaceTableProps) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const startIdx = (page - 1) * pageSize + 1;
  const endIdx = Math.min(total, page * pageSize);

  const sortBy = (key: WorkspaceSortKey) => {
    const nextOrder: 'asc' | 'desc' = sort === key && order === 'desc' ? 'asc' : 'desc';
    onParamsChange({ ...params, sort: key, order: nextOrder, page: 1 });
  };

  return (
    <div
      data-testid="workspace-table"
      className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm"
    >
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-3 text-left">製品</th>
              <th className="px-4 py-3 text-left">ws 名 / slug</th>
              <th className="px-4 py-3 text-left">課金</th>
              {SORT_COLUMNS.map((k) => (
                <th
                  key={k}
                  className="px-4 py-3 text-left"
                  aria-sort={sort === k ? (order === 'asc' ? 'ascending' : 'descending') : 'none'}
                >
                  <button
                    type="button"
                    onClick={() => sortBy(k)}
                    className="font-medium uppercase tracking-wider hover:text-slate-700"
                  >
                    {SORT_LABEL[k]}
                    {sort === k ? (order === 'asc' ? ' ▲' : ' ▼') : ''}
                  </button>
                </th>
              ))}
              <th className="px-4 py-3 text-left">AI</th>
              <th className="px-4 py-3 text-left">Ch</th>
              <th className="px-4 py-3 text-left">AI採用</th>
              <th
                className="px-4 py-3 text-left"
                title="製品に関するお知らせメールの許可。Owner のうち誰かが許可していれば「許可」（案内メールが届く）"
              >
                お知らせ許可
              </th>
              <th className="px-4 py-3 text-left">シグナル</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 && (
              <tr>
                <td colSpan={12} className="px-4 py-6 text-center text-sm text-slate-500">
                  該当する workspace がありません。
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.workspaceId} className="hover:bg-slate-50">
                <td className="px-4 py-3">
                  <span className="rounded bg-slate-100 px-2 py-0.5 text-xs">{PRODUCT_LABEL}</span>
                </td>
                <td className="px-4 py-3">
                  <Link
                    to={`/workspaces/${encodeURIComponent(r.workspaceId)}`}
                    className="font-medium text-sky-700 hover:underline"
                    data-testid="workspace-detail-link"
                  >
                    {r.displayName}
                  </Link>
                  <div className="text-xs text-slate-500">{r.slug}</div>
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`rounded px-2 py-0.5 text-xs ${STATUS_BADGE[r.billingStatus] ?? 'bg-slate-100 text-slate-600'}`}
                  >
                    {BILLING_STATUS_LABEL[r.billingStatus]}
                  </span>
                </td>
                <td className="px-4 py-3 text-slate-700">{formatRelative(r.lastLoginAt)}</td>
                <td className="px-4 py-3 text-slate-700">{formatRelative(r.createdAt)}</td>
                <td className="px-4 py-3 text-slate-700">{r.leadsLast7d}</td>
                <td className="px-4 py-3 text-slate-700">{r.leadsLast30d}</td>
                <td className="px-4 py-3 text-slate-700">
                  {r.aiEnabled ? (
                    <span className="text-emerald-700">ON</span>
                  ) : (
                    <span className="text-slate-400">OFF</span>
                  )}
                </td>
                <td className="px-4 py-3 text-slate-700">{r.channelCount}</td>
                <td className="px-4 py-3 text-slate-700">
                  {r.aiAdoptionRate === null ? '—' : `${Math.round(r.aiAdoptionRate * 100)}%`}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`rounded px-2 py-0.5 text-xs ${PRODUCT_EMAIL_CONSENT_BADGE[r.ownerProductEmailConsent]}`}
                  >
                    {PRODUCT_EMAIL_CONSENT_LABEL[r.ownerProductEmailConsent]}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {r.healthSignals.map((s: HealthFilterKey) => (
                      <span
                        key={s}
                        className="rounded bg-rose-50 px-1.5 py-0.5 text-xs text-rose-700"
                      >
                        {HEALTH_LABEL[s]}
                      </span>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3 text-xs text-slate-600">
        <span>{total === 0 ? '0 件' : `${startIdx}-${endIdx} / 全 ${total} 件`}</span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => onParamsChange({ ...params, page: page - 1 })}
            className="rounded border border-slate-300 px-2 py-1 disabled:opacity-40"
          >
            前へ
          </button>
          <span>
            {page} / {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => onParamsChange({ ...params, page: page + 1 })}
            className="rounded border border-slate-300 px-2 py-1 disabled:opacity-40"
          >
            次へ
          </button>
        </div>
      </div>
    </div>
  );
}
